import React from 'react';
import { Typography, Stack, Tooltip } from '@mui/material';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import CaptionValue from '../../CaptionValue/CaptionValue';
import StatusInfo from '../../ContactListWrapper/EntryComponent/StatusInfo';
import StatusChip from '../../Chip/StatusChip';
import Chip from '../../Chip';
import IconValue from '../../IconValue/IconValue';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import resolveValuePath from '../../../Helpers/resolveValuePath';
import {
  registryFlag,
  registryList,
  entriesForType,
  CONTENT,
  LAYOUT,
} from '../registry';
import { resolveIcon } from '../../../Utils/constants/icons';
import {
  createNewDate,
  culture,
  getAge,
  options as dateOptions,
} from '../../../Helpers/dates';
import { toTelHref } from '../../../Helpers/calls';

const PHONE_LINK_STYLE = {
  color: 'inherit',
  fontFamily: 'Roboto',
  fontWeight: 600,
  fontSize: '0.95rem',
  letterSpacing: '0.02em',
  textDecoration: 'none',
};

/** The entry a declared vocabulary holds for a stored value. */
const option = (options, value) =>
  (options ?? []).find((entry) => entry?.value === value) ?? null;

/** The label a declared vocabulary gives a stored value. */
const optionLabel = (options, value) => option(options, value)?.label ?? null;

/** Whether a stored value reads as true, held as text or as a boolean. */
const isTrue = (value) => value === true || value === 'true' || value === '1';

/**
 * The cells a layout may choose from, and the catalogue data types each suits.
 *
 * Read by the layout editor to offer the sensible cells for a column; `types` empty means the cell
 * fits any type. A cell a page registers itself is not listed here.
 *
 * `settings` is what the cell reads of the entry beyond the value, in the vocabulary of
 * `engine/settings.js`: the editor offers exactly these, so a cell that comes to read something new
 * becomes configurable by saying so here.
 */
export const CELL_TYPES = [
  { cell: 'text', label: 'CELL_TEXT', types: ['text', 'number'] },
  {
    cell: 'currency',
    label: 'CELL_CURRENCY',
    types: ['number'],
    settings: [
      {
        name: 'unit',
        label: 'CURRENCY_UNIT',
        control: 'text',
        placeholder: 'EUR',
        category: CONTENT,
      },
    ],
  },
  { cell: 'title', label: 'CELL_TITLE', types: ['text'], usesCaption: true },
  {
    cell: 'customerName',
    label: 'CELL_NAME',
    types: ['text'],
    settings: [
      { name: 'chipsKey', label: 'CHIPS_COLUMN', control: 'key', category: CONTENT },
      {
        name: 'warningKey',
        label: 'WARNING_COLUMN',
        control: 'key',
        category: CONTENT,
      },
      {
        name: 'warningLabel',
        label: 'WARNING_LABEL',
        control: 'text',
        category: CONTENT,
      },
      {
        name: 'warningIcon',
        label: 'WARNING_ICON',
        control: 'icon',
        category: LAYOUT,
      },
    ],
  },
  {
    cell: 'optionLabel',
    label: 'CELL_OPTION',
    types: ['text', 'number'],
    usesOptions: true,
    optionFields: ['label'],
  },
  {
    cell: 'iconValue',
    label: 'CELL_ICON',
    types: ['text', 'flag', 'number'],
    usesOptions: true,
    optionFields: ['label', 'icon', 'color'],
    settings: [
      { name: 'iconOnly', label: 'ICON_ONLY', control: 'switch', category: LAYOUT },
      {
        name: 'diameter',
        label: 'DIAMETER',
        control: 'text',
        placeholder: '20px',
        category: LAYOUT,
        when: (entry) => Boolean(entry?.iconOnly),
      },
    ],
  },
  {
    cell: 'status',
    label: 'CELL_STATUS',
    types: ['text', 'flag', 'number'],
    usesOptions: true,
    optionFields: ['label', 'color'],
    settings: [
      { name: 'plain', label: 'PLAIN_VALUE', control: 'switch', category: LAYOUT },
    ],
  },
  { cell: 'date', label: 'CELL_DATE', types: ['date'], usesToKey: true },
  {
    cell: 'dateAge',
    label: 'CELL_DATE_AGE',
    types: ['date'],
    settings: [
      {
        name: 'ageLabel',
        label: 'AGE_LABEL',
        control: 'text',
        placeholder: 'YEARS',
        category: CONTENT,
      },
    ],
  },
  {
    cell: 'monthChip',
    label: 'CELL_MONTH',
    types: ['date'],
    settings: [
      { name: 'color', label: 'COLOR', control: 'colour', category: LAYOUT },
    ],
  },
  { cell: 'phone', label: 'CELL_PHONE', types: ['text'] },
  {
    cell: 'tooltipIcon',
    label: 'CELL_TOOLTIP',
    types: ['text'],
    settings: [
      {
        name: 'separator',
        label: 'VALUE_SEPARATOR',
        control: 'text',
        placeholder: ';',
        category: CONTENT,
      },
    ],
  },
];

/**
 * Whether a cell reads a value vocabulary, so the editor offers one for it.
 *
 * @param {String} cell - A cell name.
 *
 * @returns {Boolean}
 */
export const cellUsesOptions = (cell) =>
  registryFlag(CELL_TYPES, 'cell', cell, 'usesOptions');

/**
 * What a cell reads of each vocabulary entry beyond its value, so the editor offers only those.
 *
 * @param {String} cell - A cell name.
 *
 * @returns {Array}
 */
export const cellOptionFields = (cell) =>
  registryList(CELL_TYPES, 'cell', cell, 'optionFields');

/**
 * Whether a cell renders a second line reading another key of the same row, so the editor offers one
 * for it.
 *
 * @param {String} cell - A cell name.
 *
 * @returns {Boolean}
 */
export const cellUsesCaption = (cell) =>
  registryFlag(CELL_TYPES, 'cell', cell, 'usesCaption');

/**
 * Whether a cell reads a second key of the same row, so the editor offers one for it.
 *
 * @param {String} cell - A cell name.
 *
 * @returns {Boolean}
 */
export const cellUsesToKey = (cell) =>
  registryFlag(CELL_TYPES, 'cell', cell, 'usesToKey');

/**
 * What a cell reads of the entry beyond its value, as the settings the editor offers for it.
 *
 * @param {String} cell - A cell name.
 *
 * @returns {Array}
 */
export const cellSettings = (cell) =>
  registryList(CELL_TYPES, 'cell', cell, 'settings');

/**
 * The cells offered for a catalogue type, the ones that suit it first.
 *
 * Ordered rather than filtered: a key whose type the catalogue does not know, or knows as text where
 * the value is a figure, would otherwise leave the cell that draws it properly unreachable.
 *
 * @param {String} type - A catalogue entry's data type.
 *
 * @returns {Array}
 */
export const cellsForType = (type) => {
  const suited = entriesForType(CELL_TYPES, type);

  return [...suited, ...CELL_TYPES.filter((cell) => !suited.includes(cell))];
};

/**
 * The same cells in the two groups the editor shows them under: the ones that suit the type, and
 * the rest.
 *
 * Ordering alone says which cells suit a key and shows no sign of where they stop, so the boundary
 * is drawn rather than implied - as the palette draws the one between the widgets and the presets.
 * An empty group is left out.
 *
 * @param {String} type - A catalogue entry's data type.
 *
 * @returns {Array} `{ label, cells }` groups, the suited ones first.
 */
export const cellGroupsForType = (type) => {
  const suited = entriesForType(CELL_TYPES, type);

  return [
    { label: 'SUITED_CELLS', cells: suited },
    {
      label: 'OTHER_CELLS',
      cells: CELL_TYPES.filter((cell) => !suited.includes(cell)),
    },
  ].filter(({ cells }) => cells.length > 0);
};

/**
 * Cell renderers, by the `cell` a column layout declares. The column counterpart of
 * `FieldComponents`.
 *
 * Each renderer receives the resolved value, the whole row, and:
 *
 *   field     - the layout entry, carrying its own options, colours and captions
 *   context   - runtime values the page published, for what a saved layout cannot hold
 *   translate - the react-intl accessor
 *
 * A page adds or overrides a cell through `resolveColumns`' `renderers`.
 */
export const CellRenderers = {
  /**
   * The value as it stands, capitalised where the entry asks. A figure is rendered as text so that
   * zero shows as zero rather than as the fallback the surfaces draw for an empty value.
   */
  text: (value, data, { field }) => {
    if (typeof value === 'number') return String(value);

    return field.capitalize ? capitalize(value) : value;
  },

  // Superseded by `status` over a vocabulary holding only the false case. Kept so a layout saved
  // naming it still draws.
  falseFlag: (value, data, options) =>
    CellRenderers.status(value, data, {
      ...options,
      field: {
        ...options.field,
        options: [
          {
            value: false,
            label: options.field.falseLabel ?? 'INACTIVE',
            color: options.field.color ?? 'error.light',
          },
        ],
      },
    }),

  // Superseded by `text`, which now renders a figure as one. Kept so a layout saved naming it still
  // draws.
  number: (value, data, options) => CellRenderers.text(value, data, options),

  /** An amount, to two places and grouped, followed by the currency `unit` names. */
  currency: (value, data, { field }) =>
    `${Number.isFinite(Number(value)) ? parseFloat(Number(value).toFixed(2)).toLocaleString(culture) : 0} ${field.unit ?? 'EUR'}`,

  // Superseded by `iconValue` with `iconOnly`. Kept so a layout saved naming it still draws.
  dot: (value, data, { field, ...rest }) =>
    CellRenderers.iconValue(value, data, {
      ...rest,
      field: { ...field, iconOnly: true },
    }),

  /** Bold name with an optional second line reading another key of the same row. */
  title: (value, data, { field }) => (
    <ColumnFlexBox>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {capitalize(value)}
      </Typography>
      {field.caption && (
        <CaptionValue
          direction="row"
          gap="4px"
          caption={field.caption.label ?? field.caption.key}
          valueStyle={{ fontWeight: 'bold', color: 'primary.main' }}
          value={
            optionLabel(
              field.caption.options,
              resolveValuePath(data, field.caption.key)
            ) || '-'
          }
        />
      )}
    </ColumnFlexBox>
  ),

  /**
   * A name, with the campaigns the row belongs to above it and a warning icon before it.
   *
   * Reads `chipsKey` for the campaigns and `warningKey` for the date that raises the warning, so
   * what is shown is declared rather than assumed; `warningIcon` and `warningLabel` name the icon
   * and its tooltip.
   */
  customerName: (value, data, { field, translate }) => {
    const chips = field.chipsKey
      ? resolveValuePath(data, field.chipsKey)
      : null;
    const warning = field.warningKey
      ? resolveValuePath(data, field.warningKey)
      : null;
    const WarningIcon = resolveIcon(field.warningIcon);

    return (
      <ColumnFlexBox gap="6px">
        {chips?.length > 0 && (
          <Stack direction="row" spacing={1}>
            {chips.map(({ description }) => (
              <Chip
                key={description}
                label={description}
                color="transparent"
                height="unset"
              />
            ))}
          </Stack>
        )}
        <CenteredFlexBox gap="4px">
          {warning && (
            <Tooltip
              placement="top"
              slotProps={{
                tooltip: { sx: { fontSize: '1rem', padding: '8px' } },
              }}
              title={`${translate(field.warningLabel)} ${createNewDate(
                warning
              ).toLocaleDateString(culture, dateOptions)}`}
              arrow
            >
              <WarningIcon sx={{ color: 'error.main' }} />
            </Tooltip>
          )}
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {capitalize(value)}
          </Typography>
        </CenteredFlexBox>
      </ColumnFlexBox>
    );
  },

  /** An icon whose tooltip lists the value's parts, and nothing at all when there is no value. */
  tooltipIcon: (value, data, { field }) => {
    if (!value) return <div />;

    const Icon = resolveIcon(field.icon);
    const items = field.separator
      ? String(value).split(field.separator)
      : [String(value)];

    return (
      <Tooltip
        title={
          <ul>
            {items.map((item) => (
              <li key={item} style={{ fontSize: '1rem' }}>
                {item}
              </li>
            ))}
          </ul>
        }
        placement="top"
        arrow
      >
        <Icon sx={{ opacity: 0.6 }} />
      </Tooltip>
    );
  },

  /** The value's declared label, or the value itself when the vocabulary has no entry for it. */
  optionLabel: (value, data, { field, translate }) => {
    const label = optionLabel(field.options, value);

    return label ? translate(label) : (value ?? '');
  },

  // Superseded by `status` with `plain`. Kept so a layout saved naming it still draws.
  flag: (value, data, { translate }) => translate(isTrue(value) ? 'YES' : 'NO'),

  /** The value with the icon its vocabulary declares for it. */
  iconValue: (value, data, { field, translate }) => {
    const entry = option(field.options, value);

    // `iconOnly` draws the marker alone, its wording becoming the tooltip: what a status light is,
    // as against a value with an icon beside it. `diameter` sizes it.
    if (field.iconOnly) {
      const Icon = resolveIcon(entry?.icon ?? 'FiberManualRecord');

      return (
        <Tooltip arrow title={translate(entry?.label ?? '')}>
          <Icon
            sx={{
              color: entry?.color ?? 'text.disabled',
              fontSize: field.diameter ?? '20px',
              display: 'block',
            }}
          />
        </Tooltip>
      );
    }

    return (
      <IconValue
        icon={entry?.icon}
        color={entry?.color}
        colorKey={value}
        value={entry?.label ? translate(entry.label) : value}
        label={field.label}
      />
    );
  },

  // The label comes from the value's own message key, as it always has; the vocabulary supplies the
  // colour, and `colors` still works for a layout that carries one.
  status: (value, data, { field, translate }) => {
    const declared = field.options ?? [];
    const colors = { ...(field.colors ?? {}) };

    declared.forEach((entry) => {
      if (entry?.color) colors[String(entry.value).toUpperCase()] = entry.color;
    });

    // A vocabulary says which values are drawn at all: one it does not hold draws nothing, which is
    // how a marker for a single case - inactive, overdue - is expressed.
    const entry = declared.length
      ? (option(declared, value) ?? option(declared, isTrue(value)))
      : null;

    if (declared.length > 0 && !entry) return <div />;

    // A flag with no vocabulary reads as yes or no; anything else reads as itself.
    const shown = entry
      ? (entry.label ?? String(entry.value))
      : typeof value === 'boolean'
        ? value
          ? 'YES'
          : 'NO'
        : value;

    if (!shown) return <div />;

    // `plain` is the wording without the marker beside it, for the properties a colour would only
    // decorate.
    if (field.plain) return translate(shown);

    return (
      <StatusInfo
        value={entry ? String(entry.value) : shown}
        label={entry?.label}
        optionsColors={colors}
      />
    );
  },

  /**
   * A date, or the span from it to the one `toKey` names.
   *
   * One end alone still reads: a span whose other end is unknown is drawn open rather than empty.
   */
  date: (value, data, { field }) => {
    const shown = (held) =>
      held ? createNewDate(held).toLocaleDateString(culture, dateOptions) : '';
    const from = shown(value);

    if (!field.toKey) return from;

    const to = shown(resolveValuePath(data, field.toKey));

    return from || to ? `${from} - ${to}` : '';
  },

  /** A date of birth, with the age it works out to. */
  dateAge: (value, data, { field, translate }) =>
    value
      ? `${createNewDate(value).toLocaleDateString(culture, dateOptions)} (${getAge(
          new Date(value)
        )} ${translate(field.ageLabel ?? 'YEARS')})`
      : '',

  // Superseded by `date`, which draws a span when it is given a second key. Kept so a layout saved
  // naming it still draws.
  dateSpan: (value, data, options) => CellRenderers.date(value, data, options),

  /** A date as a chip labelled with its month and year. */
  monthChip: (value, data, { field }) => (
    <StatusChip
      alternativeLabel={capitalize(
        `${createNewDate(value).toLocaleDateString(culture, {
          month: 'long',
          year: 'numeric',
        })}`
      )}
      status={field.key}
      options={{ [field.key]: null }}
      colors={{ [field.key]: field.color ?? '#c6cbf0' }}
    />
  ),

  /** A dialable number, when the branch is known and holds a prefix. */
  phone: (value, data, { context }) =>
    context?.userBranches?.length > 1 && context?.selectedBranch && value ? (
      <a
        style={PHONE_LINK_STYLE}
        href={toTelHref(context.selectedBranch, value)}
      >
        {value}
      </a>
    ) : (
      value
    ),
};
